/*************************************************************************
 *  Copyright 2018 Adobe Systems Incorporated. All rights reserved.
 *  This file is licensed to you under the Apache License, Version 2.0 (the "License");
 *  you may not use this file except in compliance with the License. You may obtain a copy
 *  of the License at http://www.apache.org/licenses/LICENSE-2.0
 *
 *  Unless required by applicable law or agreed to in writing, software distributed under
 *  the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 *  OF ANY KIND, either express or implied. See the License for the specific language
 *  governing permissions and limitations under the License.
 **************************************************************************/
/**
 * @file
 */

// eslint-disable-next-line import/named
import { discovery } from './Discovery';
import { logging } from './LoggingAPI';

const logger = logging.getLogger('Analytics');

export interface IAnalyticsConfig {
  getConsent?: () => boolean;
  getConsentAsync?: (category?: string) => Promise<boolean>;
  setContextData?: (contextData: IContextData) => void;
  unsetContextData?: () => void;
  userTags?: string[];
  addUserTags?: (...userTags: string[]) => void;
  setUserTags?: (userTags: string[]) => void;
}

export interface IContextData {
  content: any;
  custom: object;
  session: string;
  user: any;
  verb: string;
  source: string;
}

export interface IAnalytics {
  configure: (config: IAnalyticsConfig, sendEvent: (eventData: object, options?: object) => Promise<void>) => void;
  sendEvent: (eventData: object, options?: object) => Promise<void>;
  getEventSender: (id: string, eventConfigPath: string | null) => (eventData: object, options?: object) => void;
  getConsentCategory: (category: string) => boolean;
  getConsentCategoryAsync: (category: string) => Promise<boolean>;
  getConsent: () => boolean;
  setContextData: (contextData: IContextData) => void;
  unsetContextData: () => void;
  getEventsConfigPath: (id: string) => string | null;
}

/**
 * @classdesc
 * This is a temporary shell implementation that will allow
 * dropins to begin coding their analytics.  Implementation to follow.
 * @class
 */
class Analytics implements IAnalytics {
  _maxInitialEvents = 50;
  _initialEvents = [];
  _config: IAnalyticsConfig;
  _initialized;
  _consent;
  _getConsent:(category?: string) => boolean;
  _getConsentAsync:(category?: string) => Promise<boolean>;
  _setUserTags: (tags: string[]) => void;
  _addUserTags: (...tags: string[]) => void;
  _setContextData;
  _unsetContextData;
  _dropinIdToRepoMap: Map<string, string> = new Map();

  _ecid;

  _alloyOn = window.adobe_dc_sdk?.launchOptions?.alloyOn;

  /**
   * Configures the Analytics provider and then sends all initial events.
   * Once this method is called with both arguments, all subsequent calls will be ignored.
   * @method
   * @param {object} config -- the new configuration
   * @param {function} sendEvent -- the new sendEvent function
   * @public
   */
  configure(config: IAnalyticsConfig, sendEvent?: (eventData: object, options?: object) => Promise<void>) {
    if (!this._initialized) {
      this._initialized = !!(config && sendEvent);
      if (config) {
        this._config = config;
        if (typeof config.getConsent === 'function') {
          this._getConsent = config.getConsent;
        }
        if (typeof config.getConsentAsync === 'function') {
          this._getConsentAsync = config.getConsentAsync;
        }
        if (typeof config.setContextData === 'function') {
          this._setContextData = config.setContextData;
        }
        if (typeof config.unsetContextData === 'function') {
          this._unsetContextData = config.unsetContextData;
        }
        if (typeof config.addUserTags === 'function') {
          this._addUserTags = config.addUserTags;
        }
        if (typeof config.setUserTags === 'function') {
          this._setUserTags = config.setUserTags;
        }
      }
      if (sendEvent) {
        this.sendEvent = sendEvent;
        this._initialEvents.forEach(event => {
          this.sendEvent(event.eventData, event.options);
        });
        this._initialEvents.length = 0;
      }
    }
  }

  /**
   * Parses the version manifest to create a mapping of dropin/provider IDs to their deployment IDs
   * @method
   * @private
   */
  private _parseVersionManifest() {
    try {
      const versionManifest = window.adobe_dc_sdk?.cache?.version_manifest;
      if (!versionManifest) {
        return;
      }

      Object.entries(versionManifest).forEach(([deploymentId, entry]) => {
        if (!entry?.dropins && !entry?.providers) {
          return;
        }

        // Map each dropin ID to its deployment ID
        if (entry.dropins) {
          Object.keys(entry.dropins).forEach(dropinId => {
            this._dropinIdToRepoMap.set(dropinId, deploymentId);
          });
        }

        // Map each provider ID to its deployment ID
        if (entry.providers) {
          Object.keys(entry.providers).forEach(providerId => {
            this._dropinIdToRepoMap.set(providerId, deploymentId);
          });
        }
      });
    } catch (error) {
      logger.warn('Failed to parse version manifest', { error });
    }
  }

  /**
   * Sends the analytics event by just adding it to initial list
   * @method
   * @param {object} eventData -- payload of an analytics event
   * @returns {object} - a promise which resolves if adding to initial list is successful
   * @public
   */
  sendEvent(eventData, options) {
    if (this._initialEvents.length < this._maxInitialEvents) {
      this._initialEvents.push({ eventData, options });
      return Promise.resolve();
    }
    logger.error('Reached maximum number of initial events');
    return Promise.resolve();
  }

  /**
   * Get a logger that will add name/version to all analytics requests
   * @method
   * @param {string} id -- id of the dropin (or provider).  To be used as source.name
   * @param {string|null} [eventsConfigPath=null] -- optional path to the event config file
   * @returns {function} - a logger function that wraps sendEvent
   * @public
   */
  getEventSender(id: string, eventsConfigPath: string | null = null) {
    if (!id) {
      throw ReferenceError('missing dropin id');
    }

    // Parse version manifest on first call if not already done
    if (this._dropinIdToRepoMap.size === 0) {
      this._parseVersionManifest();
    }

    return (eventData: any, options: object = {}) => {
      if (!eventData.source) {
        eventData.source = {};
      }

      // Set source name using cached deployment ID if available, otherwise use provided id
      eventData.source.name = this._dropinIdToRepoMap.get(id) || eventData.source.name || id;

      // code defensively so that unit tests that don't load discovery won't fail
      if (!eventData.source.version && discovery.dropins[id]) {
        eventData.source.version = discovery.dropins[id].version;
      } else if (!eventData.source.version && discovery.providers[id]) {
        eventData.source.version = discovery.providers[id].version;
      }

      const updatedOptions = {
        ...options,
        eventsConfigPath,
      };

      return this.sendEvent(eventData, updatedOptions);
    };
  }

  /**
   * Retrieves the consent status for a given category in Alloy.
   *
   * @param {string} category - The category for which to retrieve the consent status.
   * @returns {boolean} - The consent status for the given category. If no consent data it will return true
   */
  getConsentCategory(category: string): boolean {
    const cookieOptanonConsent = window._satellite?.cookie?.get('OptanonConsent');
    const { adobePrivacy } = window;
    const activeGroups = adobePrivacy?.activeCookieGroups?.();
    const consent = (
      (
        activeGroups
        && activeGroups.indexOf(category) !== -1
      )
      // otherwise use the cookie
      || (
        cookieOptanonConsent
        && cookieOptanonConsent.indexOf(`${category}:1`) !== -1
      )
    );
    return consent !== undefined ? consent : true;
  }

  /**
   * Asynchronously retrieves the consent status for a given category.
   *
   * This method first checks if the `_getConsent` function is available and uses it to get the consent status.
   * If `_getConsent` is not available, it waits for the `__satelliteLoadedPromise` to resolve and then attempts
   * to retrieve the consent status from the `window._satellite` object.
   *
   * @param {string} category - The consent category to check.
   * @returns {Promise<boolean>} A promise that resolves to `true` if the consent is granted, or rejects with an error if not.
   * @throws {Error} Throws an error if `_getConsent` or the consent category promise fails, or if the consent retrieval method is invalid.
   */
  getConsentCategoryAsync(category: string) {
    if (this._getConsentAsync) {
      return this._getConsentAsync(category).then(() => true).catch(error => {
        throw new Error(`_getConsentAsync error: ${error}`);
      });
    }
    return window.__satelliteLoadedPromise.then(() => {
      const consent = window._satellite?.getVar('consent');
      if (typeof consent === 'function' && consent[category]?.then) {
        return consent[category].then(() => true).catch(error => {
          throw new Error(`consent category error: ${error}`);
        });
      }
      throw new Error('satellite getVar consent is not a function or consent category is not a promise');
    });
  }

  /**
   * Retrieves the consent for analytics.
   *
   * @returns {boolean} The consent status for analytics.
   */
  getConsent(): boolean {
    if (this._getConsent) {
      return this._getConsent();
    }
    return this.getConsentCategory('C0002');
  }

  async getECID() {
    if (!this._ecid) {
      try {
        await window.__satelliteLoadedPromise;
        if (this._alloyOn) {
          const result = await window.alloy_getIdentity;
          if (result?.identity?.ECID) {
            this._ecid = result.identity.ECID;
          }
        } else {
          this._ecid = window.Visitor.getInstance(window._satellite.getVar(
            'marketingCloudOrganizationID')).getMarketingCloudVisitorID();
        }
      } catch (e) {
        return this._ecid;
      }
    }
    return this._ecid;
  }

  /**
   * Sets custom context data for analytics.
   * @param { object } contextData - Payload of an analytics event
   * @param { object } contextData.content - content - Optional
   * @param { object } contextData.custom  - custom  - Optional
   * @param { object } contextData.session - session - Optional
   * @param { object } contextData.user    - user    - Optional
   * @param { object } contextData.verb    - verb    - Optional
   * @param { object } contextData.source  - source  - Optional
   */
  setContextData(contextData: IContextData) {
    if (this._setContextData) {
      this._setContextData(contextData);
    }
  }

  /**
   * Unsets custom context data for analytics.
   */
  unsetContextData() {
    if (this._unsetContextData) {
      this._unsetContextData();
    }
  }

  setUserTags(userTags: string[]) {
    // eslint-disable-next-line no-unused-expressions
    this._setUserTags?.(userTags);
  }

  addUserTags(...userTags: string[]) {
    // eslint-disable-next-line no-unused-expressions
    this._addUserTags?.(...userTags);
  }

  /**
   * Gets the path to event_config.json for a given dropin or provider ID
   * @method
   * @param {string} id - ID of the dropin or provider
   * @returns {string|null} Path to event_config.json or null if path cannot be determined
   * @public
   */
  getEventsConfigPath(id: string): string | null {
    try {
      let url: string | undefined;

      // Check dropins first
      if (discovery.dropins[id]?.url) {
        url = discovery.dropins[id].url;
      } else if (discovery.providers[id]?.url) {
        // Then check providers
        url = discovery.providers[id].url;
      }

      if (!url) {
        return null;
      }

      // Replace the last part of the URL path with event_config.json
      return url.replace(/\/[^/]+$/, '/event_config.json');
    } catch (error) {
      logger.error('Error determining event config path:', error);
      return null;
    }
  }
}
const analytics = new Analytics();

export { analytics };
export default Analytics;
